import React, { useEffect, useState } from "react";

import { Box, Table as ChakraTable } from "@chakra-ui/react";
import { ReactComponent as More } from "assets/icons/more.svg";
import { ReactComponent as EmptyStateIllustration } from "assets/illustrations/empty-business-compliance-illustration.svg";
import { Maybe } from "utils/general/maybe";

import { Spinner } from "ui/spinner";
import { StyledCheckbox } from "ui/styled-checkbox";
import { Paragraph2, Paragraph3 } from "ui/typography";

import {
  StyledTableCell,
  StyledTableHeader,
  StyledTableHeaderCell,
  StyledTableRow,
  firstColumnCheckboxWrapperCss,
} from "./styles";
import { TTableProps } from "./types";

const ZERO = 0;
const ONE = 1;
const FIRST_LOADING_TIMEOUT = 1000;

export const Table = (props: TTableProps) => {
  const {
    columns,
    data,
    description,
    hasCheckbox = false,
    hasOptions,
    handleOptions,
    isRowClickable,
    onRowClick,
    isLoading,
    selectedTableRowIds,
    setSelectedTableRowIds,
    tableRef,
  } = props;
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      setIsFirstLoad(false);
    }, FIRST_LOADING_TIMEOUT);
  }, []);

  return (
    <ChakraTable.Root
      size="sm"
      variant="outline"
      ref={tableRef}
    >
      {/* Table Header */}
      <StyledTableHeader>
        <StyledTableRow>
          {columns &&
            columns.map((column, index) => (
              <StyledTableHeaderCell key={index}>
                {column}
              </StyledTableHeaderCell>
            ))}
          {hasOptions && (
            <StyledTableHeaderCell>
              <></>
            </StyledTableHeaderCell>
          )}
        </StyledTableRow>
      </StyledTableHeader>

      {/* Table Body */}
      <tbody>
        <Maybe condition={isLoading !== undefined}>
          <Maybe condition={!!isLoading || isFirstLoad}>
            <StyledTableRow>
              <StyledTableCell colSpan={columns.length}>
                <Box className="flex justify-center">
                  <Spinner size="35px" />
                </Box>
              </StyledTableCell>
            </StyledTableRow>
          </Maybe>
        </Maybe>
        <Maybe condition={!isLoading && !isFirstLoad}>
          {data?.length > ZERO ? (
            data.map((row, rowIndex) => (
              <StyledTableRow
                key={rowIndex}
                css={{
                  ...(isRowClickable && {
                    cursor: "pointer",

                    "&:hover td": {
                      backgroundColor: "#F7F7F7",
                    },
                  }),
                }}
                onClick={() => isRowClickable && onRowClick && onRowClick(row)}
              >
                {/* Render the first column with checkbox and data */}
                {columns.map((column, colIndex) => (
                  <StyledTableCell
                    key={colIndex}
                    className="relative"
                  >
                    {colIndex === ZERO && hasCheckbox ? (
                      <>
                        <Box
                          css={firstColumnCheckboxWrapperCss}
                          onClick={(evt) => evt.stopPropagation()}
                        >
                          <StyledCheckbox
                            handleCheckBoxChange={() => {
                              if (
                                selectedTableRowIds &&
                                setSelectedTableRowIds
                              ) {
                                setSelectedTableRowIds({
                                  ...selectedTableRowIds,
                                  [row?.id as string]:
                                    !selectedTableRowIds[row?.id as string],
                                });
                              }
                            }}
                            isChecked={
                              selectedTableRowIds?.[row?.id as string] || false
                            }
                            name={`rowCheckbox-${rowIndex}`}
                          />
                        </Box>
                        <Box className="ml-4">{row[column]}</Box>
                      </>
                    ) : (
                      row[column]
                    )}
                  </StyledTableCell>
                ))}
                {hasOptions && (
                  <StyledTableCell>
                    <button
                      type="button"
                      onClick={handleOptions}
                      className="p-2.5 rounded-[4px] w-9 h-9 bg-white"
                      style={{
                        // boxShadow: " 0px 0px 0px 1px rgba(18, 43, 105, 0.08)",
                        // boxShadow: "0px 1px 2px 0px rgba(18, 43, 105, 0.08)",
                        boxShadow: "0px 2px 6px 0px rgba(18, 43, 105, 0.04)",
                      }}
                    >
                      <More />
                    </button>
                  </StyledTableCell>
                )}
              </StyledTableRow>
            ))
          ) : (
            <EmptyTableUI
              description={description}
              column={columns.length || ONE}
            />
          )}
        </Maybe>
      </tbody>
    </ChakraTable.Root>
  );
};

const EmptyTableUI = ({
  description,
  column = ONE,
}: {
  description?: React.ReactNode;
  column?: number;
}) => {
  return (
    <StyledTableRow
      w="100%"
      bg="white"
    >
      <td
        colSpan={column}
        className="text-ep-gray text-center text-sm py-[71px]"
        style={{
          borderBottom: "transparent! important",
          outline: "transparent! important",
        }}
      >
        <Box className="h-[191px] flex flex-col items-center gap-3">
          <Box>
            <EmptyStateIllustration />
          </Box>
          <Maybe condition={!description}>
            <Box className="h-[78px] flex flex-col gap-2">
              <Paragraph2
                fontWeight="700"
                lineHeight="25.6px"
                textColor="#1A202C"
                className="text-center"
              >
                Nothing to see here
              </Paragraph2>
              <Paragraph3
                lineHeight="22.4px"
                textColor="#718096"
                className="text-center w-[268px]"
              >
                There is nothing to see here at the moment.
                <br />
                Please check back later.
              </Paragraph3>
            </Box>
          </Maybe>
          <Maybe condition={Boolean(description)}>{description}</Maybe>
        </Box>
      </td>
    </StyledTableRow>
  );
};

export default Table;
